// exportParcelsToExcel.ts
import XLSX from 'xlsx-js-style';
import { Parcel } from "../services/parcelTypes";

type ExportOptions = {
    fileName?: string;
    parcelsSheetName?: string;
    cityContactsSheetName?: string;
};

const formatDate = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toISOString().slice(0, 10);
};

const styleHeaderRow = (worksheet: XLSX.WorkSheet) => {
    if (!worksheet["!ref"]) return;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        const cell = worksheet[cellAddress];

        if (!cell) continue;

        cell.s = {
            font: {
                bold: true,
                color: { rgb: "000000" },
            },
            fill: {
                fgColor: { rgb: "D9E1F2" },
            },
            alignment: {
                horizontal: "center",
                vertical: "center",
                wrapText: true,
            },
            border: {
                top: { style: "thin", color: { rgb: "BFBFBF" } },
                bottom: { style: "thin", color: { rgb: "BFBFBF" } },
                left: { style: "thin", color: { rgb: "BFBFBF" } },
                right: { style: "thin", color: { rgb: "BFBFBF" } },
            },
        };
    }
};

const styleBodyRows = (worksheet: XLSX.WorkSheet) => {
    if (!worksheet["!ref"]) return;

    const range = XLSX.utils.decode_range(worksheet["!ref"]);

    for (let row = 1; row <= range.e.r; row++) {
        for (let col = range.s.c; col <= range.e.c; col++) {
            const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
            const cell = worksheet[cellAddress];

            if (!cell) continue;

            cell.s = {
                fill:
                    row % 2 === 0
                        ? { fgColor: { rgb: "F7F7F7" } }
                        : { fgColor: { rgb: "FFFFFF" } },
                alignment: {
                    vertical: "top",
                    wrapText: true,
                },
                border: {
                    top: { style: "thin", color: { rgb: "E0E0E0" } },
                    bottom: { style: "thin", color: { rgb: "E0E0E0" } },
                    left: { style: "thin", color: { rgb: "E0E0E0" } },
                    right: { style: "thin", color: { rgb: "E0E0E0" } },
                },
            };
        }
    }
};

const normalizeParcelRow = (parcel: Parcel) => ({
    PIN: parcel.pin ?? "",
    Major: parcel.components?.major ?? "",
    Minor: parcel.components?.minor ?? "",
    Site_ID: parcel.siteId ?? "",
    County_Code: parcel.countyCode ?? "",

    Situs_Address: parcel.address?.situs ?? "",
    City: parcel.address?.city ?? "",
    State: parcel.address?.state ?? "",
    Zip: parcel.address?.zip ?? "",

    Latitude: parcel.location?.lat ?? "",
    Longitude: parcel.location?.lon ?? "",

    Land_Use_Code: parcel.landUse?.code ?? "",
    Land_Use_Description: parcel.landUse?.description ?? "",
    Land_Use_Category: parcel.landUse?.category ?? "",
    Future_Land_Use: parcel.landUse?.future ?? "",
    Zoning: parcel.zoning?.code ?? "",

    Lot_Sqft: parcel.site?.lot?.sqft ?? "",
    Lot_Acres: parcel.site?.lot?.acres ?? "",
    Building_Sqft: parcel.site?.buildingSqft ?? "",
    Commercial_Count: parcel.site?.counts?.commercial ?? "",
    Residential_Count: parcel.site?.counts?.residential ?? "",
    Commercial_Gross_Sqft: parcel.site?.areaSqft?.commercialGross ?? "",
    Commercial_Net_Sqft: parcel.site?.areaSqft?.commercialNet ?? "",
    Year_Built: parcel.site?.yearBuilt ?? "",
    Is_New_Construction: parcel.site?.isNewConstruction ?? "",

    Appraised_Land: parcel.valuation?.appraised?.land ?? "",
    Appraised_Improvements: parcel.valuation?.appraised?.improvements ?? "",
    Appraised_Total: parcel.valuation?.appraised?.total ?? "",
    Taxable_Land: parcel.valuation?.taxable?.land ?? "",
    Taxable_Improvements: parcel.valuation?.taxable?.improvements ?? "",
    Taxable_Total: parcel.valuation?.taxable?.total ?? "",
    Assessed_Land: parcel.valuation?.assessed?.land ?? "",
    Assessed_Improvements: parcel.valuation?.assessed?.improvements ?? "",
    Assessed_Total: parcel.valuation?.assessed?.total ?? "",
    Assessment_Year: parcel.valuation?.assessmentYear ?? "",
    Tax_Year: parcel.valuation?.taxYear ?? "",

    Owner_Name: parcel.ownership?.ownerName ?? "",
    Property_Name: parcel.ownership?.propertyName ?? "",
    Property_Type: parcel.ownership?.propertyType ?? "",

    City_Last_Contact_Date: formatDate(parcel.cityLastContactDate),
    City_Next_Contact_Date: formatDate(parcel.cityNextContactDate),
    Owner_Contact_Name: parcel.ownerContactName ?? "",
    Owner_Contact_Phone: parcel.ownerContactPhone ?? "",
    Owner_Contact_Email: parcel.ownerContactEmail ?? "",
    Development_Plans_Exist: parcel.developmentPlansExist ?? "",
    Parcel_Notes: parcel.parcelNotes ?? "",
    Planned_Use: parcel.plannedUse ?? "",
    Planned_Dwelling_Units: parcel.plannedDwellingUnits ?? "",
    Planned_Commercial_Sqft: parcel.plannedCommercialSqft ?? "",
    Planned_Total_Sqft: parcel.plannedTotalSqft ?? "",
    Planned_Parking_Stalls: parcel.plannedParkingStalls ?? "",
    Planned_Parking_Type: parcel.plannedParkingType ?? "",
    Planned_Development_Timeline: parcel.plannedDevelopmentTimeline ?? "",
    Last_Updated_At: formatDate(parcel.lastUpdatedAt),
});

const normalizeCityContactRows = (parcels: Parcel[]) =>
    parcels.flatMap((parcel) =>
        (parcel.cityContacts ?? []).map((contact) => ({
            Parcel_PIN: parcel.pin ?? "",
            Contact_Date: formatDate(contact.contactDate),
            Staff_Name: contact.staffName ?? "",
            Staff_Category: contact.staffCategory ?? "",
            Notes: contact.notes ?? "",
            Created_At: formatDate(contact.createdAt),
            Updated_At: formatDate(contact.updatedAt),
        }))
    );

export const createParcelsWorksheet = (parcels: Parcel[]): XLSX.WorkSheet => {
    const rows = parcels.map(normalizeParcelRow);
    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet["!cols"] = [
        { wch: 14 }, // PIN
        { wch: 10 }, // Major
        { wch: 10 }, // Minor
        { wch: 10 }, // Site_ID
        { wch: 12 }, // County_Code
        { wch: 24 }, // Situs_Address
        { wch: 18 }, // City
        { wch: 8 },  // State
        { wch: 10 }, // Zip
        { wch: 12 }, // Latitude
        { wch: 12 }, // Longitude
        { wch: 14 }, // Land_Use_Code
        { wch: 28 }, // Land_Use_Description
        { wch: 18 }, // Land_Use_Category
        { wch: 20 }, // Future_Land_Use
        { wch: 18 }, // Zoning
        { wch: 12 }, // Lot_Sqft
        { wch: 10 }, // Lot_Acres
        { wch: 14 }, // Building_Sqft
        { wch: 16 }, // Commercial_Count
        { wch: 16 }, // Residential_Count
        { wch: 20 }, // Commercial_Gross_Sqft
        { wch: 18 }, // Commercial_Net_Sqft
        { wch: 10 }, // Year_Built
        { wch: 18 }, // Is_New_Construction
        { wch: 14 }, // Appraised_Land
        { wch: 20 }, // Appraised_Improvements
        { wch: 14 }, // Appraised_Total
        { wch: 12 }, // Taxable_Land
        { wch: 18 }, // Taxable_Improvements
        { wch: 12 }, // Taxable_Total
        { wch: 12 }, // Assessed_Land
        { wch: 18 }, // Assessed_Improvements
        { wch: 12 }, // Assessed_Total
        { wch: 14 }, // Assessment_Year
        { wch: 10 }, // Tax_Year
        { wch: 28 }, // Owner_Name
        { wch: 36 }, // Property_Name
        { wch: 12 }, // Property_Type
        { wch: 18 }, // City_Last_Contact_Date
        { wch: 18 }, // City_Next_Contact_Date
        { wch: 22 }, // Owner_Contact_Name
        { wch: 18 }, // Owner_Contact_Phone
        { wch: 28 }, // Owner_Contact_Email
        { wch: 20 }, // Development_Plans_Exist
        { wch: 40 }, // Parcel_Notes
        { wch: 18 }, // Planned_Use
        { wch: 18 }, // Planned_Dwelling_Units
        { wch: 20 }, // Planned_Commercial_Sqft
        { wch: 18 }, // Planned_Total_Sqft
        { wch: 20 }, // Planned_Parking_Stalls
        { wch: 18 }, // Planned_Parking_Type
        { wch: 24 }, // Planned_Development_Timeline
        { wch: 16 }, // Last_Updated_At
    ];

    worksheet["!autofilter"] = {
        ref: worksheet["!ref"] || "A1",
    };

    styleHeaderRow(worksheet);
    styleBodyRows(worksheet);

    return worksheet;
};

export const createCityContactsWorksheet = (
    parcels: Parcel[]
): XLSX.WorkSheet => {
    const rows = normalizeCityContactRows(parcels);
    const worksheet = XLSX.utils.json_to_sheet(rows);

    worksheet["!cols"] = [
        { wch: 14 }, // Parcel_PIN
        { wch: 16 }, // Contact_Date
        { wch: 24 }, // Staff_Name
        { wch: 20 }, // Staff_Category
        { wch: 40 }, // Notes
        { wch: 16 }, // Created_At
        { wch: 16 }, // Updated_At
    ];

    worksheet["!autofilter"] = {
        ref: worksheet["!ref"] || "A1",
    };

    styleHeaderRow(worksheet);
    styleBodyRows(worksheet);

    return worksheet;
};

export const exportParcelsToExcel = (
    parcels: Parcel[],
    options: ExportOptions = {}
) => {
    const workbook = XLSX.utils.book_new();

    const parcelsSheetName = options.parcelsSheetName ?? "Parcels";
    const cityContactsSheetName =
        options.cityContactsSheetName ?? "City Contacts";
    const fileName =
        options.fileName ??
        `parcels_export_${new Date().toISOString().slice(0, 10)}.xlsx`;

    const parcelsWorksheet = createParcelsWorksheet(parcels);
    XLSX.utils.book_append_sheet(workbook, parcelsWorksheet, parcelsSheetName);

    const hasCityContacts = parcels.some(
        (parcel) =>
            Array.isArray(parcel.cityContacts) && parcel.cityContacts.length > 0
    );

    if (hasCityContacts) {
        const cityContactsWorksheet = createCityContactsWorksheet(parcels);
        XLSX.utils.book_append_sheet(
            workbook,
            cityContactsWorksheet,
            cityContactsSheetName
        );
    }

    XLSX.writeFile(workbook, fileName);
};